import { Button, ErrorCard, Image } from '@starsoft/common/components';
import { WithdrawMethodId } from '@starsoft/common/enums';
import {
  useTranslation,
  useWithdrawalCoinBySearchParamId,
  useWithdrawForm,
  useWithdrawSearchParams,
} from '@starsoft/common/hooks';
import { CoinType, GenericError } from '@starsoft/common/models';
import {
  MoneyService as Money,
  WalletService,
} from '@starsoft/common/services';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserPaymentDetailsById } from '../../../../../../../api';
import { WithdrawFormCreateDto } from '../../../../../../../dto/withdrawals/baseWithdrawalInputDto/withdraw-create-form.dto';
import { SecurityDrawerProps } from '../props';
import { getUserPaymentDetailsFieldByTypeId } from './helper';
import SecurityDrawerContentSkeleton from './Skeleton';
import styles from './styles.module.scss';

/**
 * SecurityDrawerContent Component
 * Renders the content for the security drawer, including withdrawal details and a submit button.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Loading state for the mutation process
 * @returns {JSX.Element} A container with withdrawal details and a submit button
 *
 * @example
 * <SecurityDrawerContent isLoading={false} />
 */
export default function SecurityDrawerContent({
  isLoading: isLoadingMutation,
}: Pick<SecurityDrawerProps, 'isLoading'>) {
  const { t } = useTranslation('withdraw');
  const { watch, setValue } = useFormContext<WithdrawFormCreateDto>();
  const address: string | undefined = watch('crypto.address');
  const paymentDetailsId = watch('paymentDetailsId');
  const { withdrawMethodId, coinType } = useWithdrawSearchParams();
  const {
    coin,
    error: errorCoin,
    refetch: refetchCoin,
    isPending: isPendingCoin,
  } = useWithdrawalCoinBySearchParamId();
  const {
    data: userPaymentDetail,
    error: errorUserPaymentDetail,
    isLoading: isLoadingUserPaymentDetail,
    refetch: refetchUserPaymentDetail,
  } = useUserPaymentDetailsById({
    id:
      !!paymentDetailsId && coinType === CoinType.Fiat
        ? (parseToBigInt(paymentDetailsId) as bigint)
        : undefined,
  });

  const {
    feeQuery: { errorFee, fee, isLoadingFee, refetchFee },
  } = useWithdrawForm(watch, setValue);

  const isLoading: boolean =
    isPendingCoin ?? isLoadingUserPaymentDetail ?? isLoadingFee;
  const error: AxiosError<GenericError> =
    errorCoin ?? errorUserPaymentDetail ?? errorFee;

  /**
   * Formats the address by showing the first 6 and last 4 characters.
   *
   * @param {string} address - The address to format
   * @returns {string} The formatted address
   *
   * @example
   * formatAddress('0x1234567890abcdef') // returns '0x1234...cdef'
   */
  function formatAddress(address: string): string {
    return `${address.substring(0, 6)}...${address.substring(
      address.length - 4,
    )}`;
  }

  /**
   * Gets the value to which the withdrawal is being made.
   *
   * @returns {string} The withdrawal destination value
   *
   * @example
   * getWithdrawToValue() // returns '0x1234...cdef' or a masked payment detail
   */
  function getWithdrawToValue(): string {
    switch (withdrawMethodId?.toString()) {
      case WithdrawMethodId.BetterbroArgentineBankTransfer?.toString():
      case WithdrawMethodId.BetterbroBrazillianPix?.toString():
      case WithdrawMethodId.BetterbroIndianImps?.toString():
      case WithdrawMethodId.BetterbroMexicanSpei?.toString():
      case WithdrawMethodId.PixtopayBrazillianPix?.toString():
      case WithdrawMethodId.PayGamesIndiaUpi?.toString():
      case WithdrawMethodId.PayGamesIndonesiaQris?.toString():
      case WithdrawMethodId.PayGamesNigeria?.toString():
      case WithdrawMethodId.PayGamesSingaporePayNow?.toString():
      case WithdrawMethodId.PayGamesSouthAfrica?.toString():
      case WithdrawMethodId.SevenPay?.toString():
      default:
        return getUserPaymentDetailsFieldByTypeId(userPaymentDetail);
      case WithdrawMethodId.Crypto?.toString():
        return formatAddress(address ?? '');
    }
  }

  /**
   * Gets the fee amount for the withdrawal.
   *
   * @returns {string} The formatted fee amount
   *
   * @example
   * getFeeAmount() // returns '0.001 BTC'
   */
  function getFeeAmount(): string {
    const feeAmount: Money = new Money({
      amount: getMoneyOrAmount(fee?.fee),
      coin,
    });

    feeAmount.add(getMoneyOrAmount(fee?.chainFee));

    return feeAmount.maskedAmount;
  }

  const feeAmount: string = useMemo(getFeeAmount, [fee, coin]);

  /**
   * Refetches the data in case of an error.
   */
  function refetch() {
    if (errorCoin) {
      refetchCoin();
    }

    if (errorUserPaymentDetail) {
      refetchUserPaymentDetail();
    }

    if (errorFee) {
      refetchFee();
    }
  }

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  if (isLoading) {
    return <SecurityDrawerContentSkeleton />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__card}>
        <div className={styles.container__card__row}>
          <span className={styles.container__card__row__label}>
            {t('crypto_withdraw_currency')}
          </span>

          <div
            className={`${styles.container__card__row} ${styles['container__card__row--small']}`}
          >
            <Image
              className={styles.container__card__row__coin}
              alt={`coin-icon-${coin?.name}`}
              src={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${coin?.image}`}
            />
            <span className={styles.container__card__row__value}>
              {coin?.code}
            </span>
          </div>
        </div>

        <div className={styles.container__card__row}>
          <span className={styles.container__card__row__label}>
            {t('withdraw_to')}
          </span>

          <span className={styles.container__card__row__value}>
            {getWithdrawToValue()}
          </span>
        </div>

        <div className={styles.container__card__row}>
          <span className={styles.container__card__row__label}>{t('fee')}</span>

          <span className={`${styles.container__card__row__value}`}>
            {feeAmount}
          </span>
        </div>

        <div className={styles.container__card__row}>
          <span className={styles.container__card__row__label}>
            {t('sended_amount')}
          </span>

          <span className={`${styles.container__card__row__value}`}>
            {WalletService.maskCurrency({
              amount: getMoneyOrAmount(fee?.receiveAmount),
              coin,
            })}
          </span>
        </div>
      </div>

      <Button type="submit" isloading={isLoadingMutation}>
        {t('continue')}
      </Button>
    </div>
  );
}
